import type { ApiResponse } from '@shared/types'
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { queryClient } from 'v2/configs/rq-setup'
import { aciIntegrationsService } from 'v2/shared/services/aci-integrations-service'
import type { CustomField } from 'v2/shared/services/external-apps-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { externalAppsQueryKeys, flowQueryKeys } from 'v2/shared/utils/query-key-management'
import type { OrgAppConnectionPayload, RExternalOrgApp } from 'v2/types/api/settings'
import { errorToast, successToast } from 'v2/utils/toasts'

export const invalidateExternalAppQueries = (client: QueryClient = queryClient) => {
  client.invalidateQueries({ queryKey: externalAppsQueryKeys().externalOrgAppsBase })
  client.invalidateQueries({ queryKey: flowQueryKeys().appVariablesBase })
}

export const useReconnectOrgAppConnectionMutation = () =>
  useMutation({
    mutationFn: async ({ appId }: { appId: string }) => {
      const { data } = await aciIntegrationsService.reconnect(appId, window.location.href)
      return { oauth_redirect_url: data.oauth_redirect_url ?? data.consent_url }
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to reconnect app',
      })
    },
  })

export const useUpdateOrgAppConnectionMutation = () =>
  useMutation({
    mutationFn: async ({ payload, appId }: { payload: OrgAppConnectionPayload; appId: string }) =>
      (await aciIntegrationsService.updateConnection(appId, { enabled: payload.enabled })).data,
    onSuccess: () => invalidateExternalAppQueries(),
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to update connection',
      })
    },
  })

export const useDeleteOrgAppConnectionMutation = () =>
  useMutation({
    mutationFn: async ({ appId }: { appId: string }) =>
      (await aciIntegrationsService.deleteConnection(appId)).data,
    onSettled: () => invalidateExternalAppQueries(),
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to delete connection',
      })
    },
  })

export const useUpdateCustomFieldsMutation = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({
      appId,
      fieldsByEntity,
    }: {
      appId: string
      fieldsByEntity: Record<string, CustomField[]>
    }) => (await aciIntegrationsService.updateCustomFields(appId, fieldsByEntity)).data,
    onSuccess: (_, { appId }) => {
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().customFields(appId) })
      successToast({ title: 'Success', description: 'Custom fields updated successfully' })
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to update custom fields',
      })
    },
  })
}

export const useResyncCustomFieldsMutation = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ appId, entities }: { appId: string; entities: string[] }) =>
      (await aciIntegrationsService.resyncCustomFields(appId, entities)).data,
    onSuccess: (_, { appId }) => {
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().customFields(appId) })
      successToast({ title: 'Success', description: 'Custom fields synced successfully' })
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to sync custom fields',
      })
    },
  })
}

export const useGrantAccessMutation = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ appId, payload }: { appId: string; payload?: Record<string, unknown> }) =>
      (await aciIntegrationsService.grantAccess(appId, payload)).data,
    onSuccess: (_data, { appId }) => {
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().externalOrgAppsBase })
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().accessRecords(appId) })
      successToast({ title: 'Success', description: 'Access granted successfully' })
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({ title: 'Error', description: errorText?.toString() || 'Failed to grant access' })
    },
  })
}

/** Removes every grant on one connection — the org-wide share and each user — leaving only the owner. */
export const useRevokeAllAccessMutation = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (connection: RExternalOrgApp) => {
      const { id } = connection
      if (connection.shared_with_org) await aciIntegrationsService.revokeAccess(id, { org: false })
      const users = (connection.users_with_access ?? []).filter(
        (user) => user.id !== connection.integrated_by?.id,
      )
      // One grant per call: the bridge's revoke takes a single user.
      for (const user of users) await aciIntegrationsService.revokeAccess(id, { user: user.id })
    },
    onSettled: (_data, _error, connection) => {
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().externalOrgAppsBase })
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().accessRecords(connection.id) })
    },
    onSuccess: () => successToast({ title: 'Success', description: 'Access revoked successfully' }),
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description:
          errorText?.toString() ||
          'Could not revoke every grant. Some access may already have been removed.',
      })
    },
  })
}

export const useRevokeAccessMutation = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ appId, payload }: { appId: string; payload?: Record<string, unknown> }) =>
      (await aciIntegrationsService.revokeAccess(appId, payload)).data,
    onSuccess: (_data, { appId }) => {
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().externalOrgAppsBase })
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().accessRecords(appId) })
      successToast({ title: 'Success', description: 'Access revoked successfully' })
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to revoke access',
      })
    },
  })
}
